// Shared question-set related types and constants used across all web apps.
//
// Domain-core constants (class/grade, target exam, subject) live in
// @padcanvas/shared — they apply beyond questions (students, teachers,
// modules) so they're kept separate from question-specific options.

import type { ExamVariant, OmrSheetPin, ResultRelease } from '@padcanvas/shared';
import type { MarkingScheme, MarksBreakdown, PaperRules, QuestionMarkingInput, QuestionSetConfig, QuestionSetModality, DueAt, HintLevelSetting, SessionIssue, SessionIssueKind, InputMode, SavedAnswer } from '@padcanvas/shared';
// Canonical config types now live in @padcanvas/shared (§12); re-export for existing imports.
export type { QuestionSetConfig, QuestionSetModality, SessionIssue, SessionIssueKind };

// ── Domain option lists ────────────────────────────────────────────────────

export const DIFFICULTY_OPTIONS = ['easy', 'medium', 'hard'] as const;
export type DifficultyLevel = (typeof DIFFICULTY_OPTIONS)[number];

export const PROFICIENCY_OPTIONS = ['low', 'medium', 'high'] as const;
export type ProficiencyLevel = (typeof PROFICIENCY_OPTIONS)[number];

export const SOLUTION_STYLE_OPTIONS = ['detailed', 'formal', 'quick'] as const;
export type SolutionStyleOption = (typeof SOLUTION_STYLE_OPTIONS)[number];

export const ENRICH_STATUS_LEVELS = [
  { value: 0, label: 'Not Needed' },
  { value: 1, label: 'Pending' },
  { value: 2, label: 'Basic Enriched' },
  { value: 3, label: 'Fully Enriched' },
] as const;

export const ENRICH_STATUS_LABELS: Record<number, string> = Object.fromEntries(
  ENRICH_STATUS_LEVELS.map(r => [r.value, r.label])
);

export const ASSESSMENT_LABELS: Record<string, string> = {
  correct: 'Correct',
  correct_but_inefficient: 'Correct but Inefficient',
  partially_correct: 'Partially Correct',
  partial: 'Partially Correct',
  incorrect: 'Incorrect',
};

/** The "Working" choice on a paper and on an assignment. `'default'` is the
 *  form's "not chosen": absent in the document, so the tenant's default per
 *  modality and question class decides. See working-role-target.md §2, §7. */
export type WorkingChoice = 'default' | 'marked' | 'scratch';

export const WORKING_CHOICES: { value: WorkingChoice; label: string }[] = [
  { value: 'default', label: 'Default (from tenant settings)' },
  { value: 'marked', label: 'Marked — students were told to show working' },
  { value: 'scratch', label: 'Not marked — rough work only' },
];

/** The "Default" option, saying what the tenant's default gives: one value
 *  when both question classes agree, else each class's. */
export function workingDefaultLabel(d: { freeform: 'marked' | 'scratch'; other: 'marked' | 'scratch' } | undefined): string {
  if (!d) return WORKING_CHOICES[0].label;
  const say = (v: 'marked' | 'scratch') => (v === 'marked' ? 'marked' : 'not marked');
  const value = d.freeform === d.other
    ? `${d.freeform === 'marked' ? 'Marked' : 'Not marked'} for every question`
    : `written answers ${say(d.freeform)}, MCQ / numerical ${say(d.other)}`;
  return `Default (tenant setting) — ${value}`;
}

export const WORKING_HELP =
  'Whether the working counts toward the grade, as the students were told. '
  + "By default, a test's non-free-form questions are not marked and assignments are.";

/** When a sitting's results reach the student. See result-release-target.md §2. */
export const RESULT_RELEASE_CHOICES: { value: ResultRelease; label: string }[] = [
  { value: 'automatic', label: 'Show to students straight away' },
  { value: 'teacher', label: 'Show when I release them' },
];

export const RESULT_RELEASE_HELP =
  'With "when I release them", students see only that they submitted — no score, marks, feedback or solutions — '
  + 'until you release the results from the assignment page. Feedback during the attempt is turned off.';

// ── Interfaces ─────────────────────────────────────────────────────────────

export interface MCQOption {
  label: string;
  text: string;
  imageUrl?: string | null;
  isCorrect?: boolean;
}

export interface TopLevelModule {
  id: string;
  displayName: string;
  subject: string;
  questionCount: number;
  subModuleCount: number;
  sortOrder: number;
}

// QuestionSetConfig is defined in @padcanvas/shared and re-exported above (§12).

export interface ExamPaperRef {
  exam: string;              // JEE, NEET, CBSE, ICSE, STATE
  class: string;             // '10' or '12'
  subjects: string[];        // e.g. ['physics'] or ['physics','chemistry','maths']
  date: string;              // "2024-01-27"
  session?: string;          // "Set 56/2/1", "Shift 1"
  displayString: string;     // admin-editable label shown in all UIs
}

/** A section within a QuestionSet (Phase 2): groups questions, optionally
 *  "attempt any K of N". See docs/FeatureDesigns/question-structure.md §Phase 2. */
export interface QuestionSetSection {
  id: string;
  title?: string;
  questionIds: string[];
  /** K of N — attempt any `requiredCount`. Absent ⇒ all required. */
  requiredCount?: number;
  /** Which K count: `best` by marks/score (absent), or the `first` K the
   *  student attempted. See AssessmentAndMarking/paper-stated-marking-target.md §4. */
  countPolicy?: 'best' | 'first';
  /** Tier 2: marking rule for this section. See docs/FeatureDesigns/AssessmentAndMarking/scoring-and-marking.md. */
  marking?: MarkingScheme;
  /** On an attempt's copy only: the K a teacher chose to count. */
  countedOverride?: { questionIds: string[]; by: string; at: string };
}

// Orthogonal question-set dimensions — see docs/FeatureDesigns/assignments-tests/qset-model-target.md
// (QuestionSetModality is re-exported from @padcanvas/shared above.)
export type QuestionSetPurpose = 'exam-paper' | 'topic-exercise' | 'custom' | 'duel' | 'daily-plan';
// Creator-asserted PYQ claim (NOT derived). FullPYP = complete real paper (requires pypRef);
// MixedPYP = built from previous-year questions; None = no claim. See design doc §3f.
export type QuestionSetPYQType = 'FullPYP' | 'MixedPYP' | 'None';
export type QuestionSetOwnerType = 'admin' | 'teacher' | 'student';

// ── Async fills ──────────────────────────────────────────────────────────
// A question set can exist before its questions do — the shell is created first
// and an async job fills it in. `builds` holds one state per lifecycle, keyed by
// WHAT is being built. Mirrors functions/src/questionSet/types.ts; see
// docs/FeatureDesigns/question-variations-from-questions.md.

export type BuildKind = 'questions' | 'markScheme';
export type BuildStatus = 'running' | 'done' | 'failed';

export interface BuildState {
  status: BuildStatus;
  jobId?: string;
  /** Set iff `status === 'failed'`. */
  error?: string;
}

export type QuestionSetBuilds = Partial<Record<BuildKind, BuildState>>;

export function buildState(
  set: { builds?: QuestionSetBuilds } | null | undefined,
  kind: BuildKind,
): BuildState | undefined {
  return set?.builds?.[kind];
}

export function isBuilding(set: { builds?: QuestionSetBuilds } | null | undefined, kind: BuildKind): boolean {
  return buildState(set, kind)?.status === 'running';
}

export function isBuildFailed(set: { builds?: QuestionSetBuilds } | null | undefined, kind: BuildKind): boolean {
  return buildState(set, kind)?.status === 'failed';
}

export interface QuestionSet {
  id: string;
  type?: 'test' | 'practice-set';
  title: string;
  description?: string;
  /** Student-facing reference material, shown INSIDE the solving session.
   *  Never sent to any LLM. See docs/FeatureDesigns/assignments-tests/teacher-assignment-from-pdf.md. */
  referenceMaterial?: string;
  createdBy: string;
  visibility: 'public' | 'private';
  status: 'draft' | 'published';
  /** Per-lifecycle state for everything filled in after the set was created —
   *  `questions` (document extraction or variation generation) and `markScheme`.
   *  Absent on a set whose questions were picked by hand. Mirrors the server
   *  type in `functions/src/questionSet/types.ts`; read it through the helpers
   *  below rather than by hand. */
  builds?: QuestionSetBuilds;
  /** Which source the questions were extracted from. Absent for hand-picked sets
   *  and for sets created before this field existed — callers should fall back to
   *  the source blob's extension. See docs/FeatureDesigns/assignments-tests/teacher-assignment-from-pdf.md. */
  sourceKind?: 'pdf' | 'docx' | 'images';
  sourcePdfBlobPath?: string;
  /** When the retained source file is deleted, ISO. Set only on a student's own
   *  import — seven days from upload. Only the FILE goes; the set, its questions
   *  and every attempt stay, so a row carrying this is not about to disappear.
   *  See docs/FeatureDesigns/student-paper-upload-target.md. */
  sourceExpiresAt?: string;
  /** When the source was actually deleted, ISO. Absent while the file is still
   *  there — `sourceExpiresAt` says "will go", this says "has gone". */
  sourceDeletedAt?: string;
  /** Present only on a set made by "Check a paper I've done". The client needs
   *  only whether its attempt exists yet; the paths stay server-side business.
   *  See docs/FeatureDesigns/assignments-tests/paper-check-target.md. */
  paperCheck?: {
    answersOnPaper: boolean;
    answerSourceKind?: 'images' | 'pdf';
    /** Read only for its length — how many photos the answer sheet was. */
    answerBlobPaths?: string[];
    /** The answer PDF's pages that were read, 1-based; absent = every page. */
    answerPageNumbers?: number[];
    testSessionId?: string;
    error?: string;
  };
  /** What the teacher named the uploaded file. The blob path normalises to
   *  `source.pdf` / `source.docx`, so this is the only record of the original
   *  name. Absent on imports that predate the field. */
  sourceFileName?: string;
  /** The pages of a source PDF the uploader chose to have read, 1-based —
   *  known from the moment of upload, so the page can say which while the
   *  extraction is still running. Absent when every page was read. */
  sourcePageNumbers?: number[];
  // ── Uploaded mark scheme (see AssessmentAndMarking/marking-scheme-upload.md) ──
  // Its own build lifecycle, independent of `questions`: a scheme is usually
  // attached after the paper. Never gates publish — questions without a rubric
  // simply mark by the existing fallback ladder.
  markSchemeSourceKind?: 'pdf' | 'docx';
  questionIds: string[];
  /** Phase 2: section structure; `questionIds` = concat of section questionIds. */
  sections?: QuestionSetSection[];
  /** Tier 2: test-level default marking rule. */
  marking?: MarkingScheme;
  /** The OMR sheet design its students answer on, pinned at import; absent —
   *  the tenant's default. See omr-reader-target.md §2. */
  omrSheet?: OmrSheetPin;
  /** Set once a sheet has been read with `omrSheet`; the design is then fixed. */
  omrSheetReadAt?: string;
  /** The paper's own answer-sheet expectation — `{ student: 'none' }` is "No OMR sheet". */
  answerSheet?: { student: 'none' | 'omr' | 'list' | 'export'; required: boolean };
  /** Marking rules read off the paper's instruction block — a draft until the
   *  teacher applies or dismisses it. See paper-stated-marking-target.md §5. */
  paperRules?: PaperRules;
  /** What became of the teacher's separate answer key (`from-key` imports). */
  answerKeyRead?: {
    status: 'read' | 'partial' | 'failed';
    rows: number;
    answers: number;
    solutions: number;
    failedPages?: string[];
    unmatched?: string[];
    /** Numbers under more than one heading with no clear choice — applied to none. */
    ambiguous?: string[];
    /** Each uploaded file's part; absent on reads from before solution files. */
    documents?: {
      kind: 'answerKey' | 'solutions';
      fileName?: string;
      status: 'read' | 'partial' | 'failed';
      rows: number;
      answers: number;
      solutions: number;
      unmatched?: string[];
      ambiguous?: string[];
      failedPages?: string[];
      /** Answer key only: questions its two reads disagreed on. */
      unclear?: number;
      /** Answer key only: pages only one of the two reads came back for. */
      readOncePages?: string[];
    }[];
    conflicts?: number;
    /** Questions the two reads of the answer key disagreed on. */
    unclear?: number;
    unsolved?: string[];
    solutionsGenerated?: boolean;
    /** Numbers no document gave an answer (not counting `unclear`) — the first
     *  20 — and how many there were in all. Those were answered by the model. */
    unanswered?: string[];
    unansweredCount?: number;
    /** The key check (paper-answer-key-target.md § Checking the key): how many
     *  key answers the model worked out with the key hidden, and the printed
     *  numbers where it reached a different answer (`keyReview.keyDisputed`). */
    keyCheck?: { checked: number; disputed: string[] };
    at: string;
  };
  /** What each question is worth ON THIS PAPER — questionId → marks. Only the
   *  overrides; an absent key means the question's own marks, so the same
   *  question can be worth 4 here and 3 elsewhere.
   *  See docs/FeatureDesigns/assignments-tests/per-test-question-marks-target.md. */
  questionMarks?: Record<string, number>;
  /** Set when this paper re-serves what one test report found lost, so that
   *  report's inferred `attainable` can be measured (test-report-target.md §7). */
  reattemptOf?: { reportId: string };
  config: QuestionSetConfig;
  totalQuestions: number;
  subjects: string[];
  classes?: string[];        // target grades, e.g. ['10'] or ['11','12']
  topics?: string[];         // moduleIds (any level) the assignment covers
  tags?: string[];
  targetExam?: string;       // JEE, NEET, CBSE, ICSE, STATE
  /** Which of the programme's exams the paper is — 'jee-main' | 'jee-advanced' for JEE. */
  examVariant?: ExamVariant;
  targetExams?: string[];    // topic exercises: multi-exam targeting; empty/absent = generic (all exams)
  /** @deprecated RETIRED 2026-09-08 — read and written nowhere. Every stored set
   *  now carries `pyqType` (backfill 006). Still on documents; deleted in phase 6
   *  of docs/FeatureDesigns/assignments-tests/qset-field-migration-implplan.md. */
  pypRef?: ExamPaperRef;     // present whenever pyqType === 'FullPYP'
  source?: string;           // free-text provenance
  difficulty?: 'easy' | 'medium' | 'hard' | 'mixed';
  keywords?: string[];
  timesAttempted: number;
  createdAt: string;
  updatedAt: string;
  // New dimensional fields (P1, additive & optional)
  ownerType?: QuestionSetOwnerType;
  purpose?: QuestionSetPurpose;
  pyqType?: QuestionSetPYQType;
  sortOrder?: number;
  defaultModality?: QuestionSetModality;
  allowedModalities?: QuestionSetModality[];
  practiceLocked?: boolean;             // P5: reserved for tests — members withheld from practice until attempted
}

export interface QSetSessionQuestionResult {
  questionId: string;
  submissionId?: string;
  subject: string;
  moduleId?: string;
  difficulty?: string;
  correctnessScore?: number;
  speedScore?: number;
  approachScore?: number;
  finalScore?: number;
  overallAssessment?: string;
  timeTakenSeconds?: number;
  attemptedAt?: string;
  skipped: boolean;
  /** Hint usage counters for this attempt. Mirrors the same fields on the
   *  Submission doc. See docs/FeatureDesigns/HintSystem/hint-tracking.md. */
  hintsShown?: number;
  hintsUsedLlm?: number;
  /** How the student gave the answer. Filled by the server on `get` from the
   *  submission's model input — never stored on the session. */
  inputMode?: InputMode;
  /** Lifecycle of the analysis behind this result.
   *  - undefined / 'completed': analysis ran (current behavior + sync analyze)
   *  - 'pending': background analyze enqueued; scores will land later
   *  - 'failed': analysis terminally failed (after retry budget exhausted)
   *  See docs/FeatureDesigns/async-jobs.md. */
  status?: 'pending' | 'completed' | 'failed';
  /** Why the analysis failed, set only with `status: 'failed'`. Mirrors the
   *  server's QSetSessionQuestionResult — the results table renders it in place
   *  of a score. */
  error?: { word?: string; code?: number; message: string };
  /** Phase 2: false for a section K-of-N "loser" (attempted but not among the
   *  best K) → excluded from the score. Absent ⇒ counted. */
  countedInScore?: boolean;
  // ── Tier 2: marks (test-context). See docs/FeatureDesigns/AssessmentAndMarking/scoring-and-marking.md. ──
  marksAwarded?: number;
  maxMarks?: number;
  markingInput?: QuestionMarkingInput;
  /** Marks left for a teacher — `'working'` when the working's score was
   *  withheld because the page may have been misread. */
  manualMarkingPending?: string[];
  /** `working_answer` marking: the working / final-option split behind
   *  `marksAwarded`. See paper-stated-marking-target.md §6.3. */
  marksBreakdown?: MarksBreakdown;
  /** Earliest page (0-based) of the uploaded script this question's work is on. */
  firstPageIndex?: number;
  /** First-K sections: position among the attempted questions and what decided it. */
  attemptOrder?: number;
  attemptOrderSource?: 'page' | 'time' | 'paper';
  attemptOrderTied?: true;
  /** A teacher chose which K count in this question's section. */
  countedByTeacher?: true;
}

export interface SectionBreakdown {
  sectionId: string;
  title?: string;
  totalQuestions: number;
  requiredCount: number;
  questionsAttempted: number;
  questionsCounted: number;
  questionsCorrect: number;
  avgFinalScore: number;
  /** Tier 2: marks for this section (when a scheme is set). */
  marksAwarded?: number;
  maxMarks?: number;
}

export interface SubjectBreakdown {
  subject: string;
  questionsAttempted: number;
  questionsCorrect: number;
  avgFinalScore: number;
}

export interface DifficultyBreakdown {
  difficulty: string;
  questionsAttempted: number;
  questionsCorrect: number;
  avgFinalScore: number;
}

export interface QSetSessionSummary {
  totalQuestions: number;
  questionsAttempted: number;
  questionsSkipped: number;
  questionsCorrect: number;
  questionsIncorrect: number;
  questionsPartial: number;
  avgCorrectnessScore: number;
  avgSpeedScore: number;
  avgApproachScore: number;
  avgFinalScore: number;
  /** Time spent solving, summed from the per-question times — ABSENT when any
   *  attempted question has none. Never wall clock: the server derives it on
   *  the way out, for attempts submitted before it meant this as well as after.
   *  See `solvingTimeSeconds` in @padcanvas/shared. */
  totalTimeSeconds?: number;
  subjectBreakdown: SubjectBreakdown[];
  difficultyBreakdown: DifficultyBreakdown[];
  /** Phase 2: per-section roll-up (best-K). Absent for flat sets. */
  sectionBreakdown?: SectionBreakdown[];
  /** Tier 2: exam-marks total (when the test has a marking scheme). */
  marksAwarded?: number;
  maxMarks?: number;
}

// SessionIssue now lives in @padcanvas/shared beside the review payload that
// carries it (§12 pattern); re-exported above for existing imports.

/** One durable solution scan on an attempt. Mirrors `SolutionUploadLink` in
 *  functions/src/questionSet/types.ts — only the fields the web apps read. */
export interface SolutionUploadLink {
  uploadId: string;
  /** Absent while `status === 'in-progress'`: the manifest is written when the
   *  student confirms the mapping. A scan is viewable before that — the pages
   *  are in blob storage from page one; the manifest adds the mapping. */
  manifestUrl?: string;
  pageCount: number;
  createdAt: string;
  /** Stamped `in-progress` when the upload is CREATED, not when it finishes.
   *  Absent on links written before this field existed; those are all finalized. */
  status?: 'in-progress' | 'finalized' | 'discarded';
}

export interface QSetSession {
  id: string;
  /** The student's read of an attempt whose teacher holds the results: the
   *  server took out everything about how it went. Never on a teacher's read.
   *  See docs/FeatureDesigns/assignments-tests/result-release-target.md §4. */
  heldResults?: true;
  /** Set when someone other than the student handed this attempt in — a teacher
   *  uploading a script the student solved on paper. The attempt is still the
   *  student's; this says who submitted it, and every surface showing the
   *  attempt must say so too.
   *  See docs/FeatureDesigns/roster-accounts-and-login-identity.md. */
  submittedOnBehalfBy?: string;
  /** Display name for `submittedOnBehalfBy`, resolved server-side. */
  submittedOnBehalfByName?: string;
  /** 'paper-check' — the attempt was made from a paper sat elsewhere and is
   *  marked in the background; there is nothing for the student to resume. */
  origin?: 'paper-check';
  type?: 'test' | 'practice-set';
  modality?: QuestionSetModality;   // mode this attempt plays in (stamped at start); drives play profile
  purpose?: QuestionSetPurpose;     // snapshot of the set's purpose; 'daily-plan' → the daily session profile
  /** Which screen this sitting runs on — the replica of a real exam's CBT
   *  screen, the app's own, or the worksheet (every question of one set on a
   *  page, each checked on its own). Frozen at start; only written when it is
   *  not `'standard'`, so an absent value is a standard sitting, which is every
   *  session written before the exam shell.
   *  See docs/FeatureDesigns/assignments-tests/exam-interface-target.md and
   *  docs/FeatureDesigns/chapter-practice/chapter-practice-target.md. */
  screen?: 'exam' | 'standard' | 'worksheet';
  userId: string;
  questionSetId: string;
  questionSetTitle: string;
  /** Student-facing reference material, snapshotted from the set at start.
   *  Shown during the solving session; never sent to any LLM. */
  referenceMaterial?: string;
  /** In-progress "solve on paper → upload all" for this session, if any.
   *  Lets the student resume the upload/attribution (phase-aware) after a
   *  browser close/crash. Survives finalize and discard — see `discardedAt`. */
  activeBulkUpload?: {
    sessionId: string;
    token: string;
    createdAt: string;
    /** Set when the student dismissed this upload. The stamp is NOT cleared:
     *  it is the only link from the attempt to the scan's blobs. Read through
     *  `liveBulkUpload()`, never by testing `activeBulkUpload` directly. */
    discardedAt?: string;
  };
  /** Durable solution uploads (student's original PDF/photo pages) for this
   *  attempt. Page URLs + source PDF are resolved lazily via the solutionUpload
   *  action — read through `viewableSolutionUploads()`, never by presence: an
   *  entry exists from the moment a scan STARTS, so the array also holds
   *  in-progress and discarded scans that have nothing to show.
   *  Mirrors `SolutionUploadLink` in functions/src/questionSet/types.ts.
   *  See docs/FeatureDesigns/view-uploaded-solution.md. */
  solutionUploads?: SolutionUploadLink[];
  /** What became of a solution the student uploaded from the assignment page —
   *  the one field that page reads to know whether to show progress, a review
   *  prompt, or a retry. See docs/FeatureDesigns/assignments-tests/assignment-solution-upload.md. */
  solutionUploadState?: {
    status: 'extracting' | 'splitting' | 'attributing' | 'analyzing' | 'ready' | 'failed';
    intent: 'submit' | 'save';
    uploadId: string;
    updatedAt: string;
    /** Student-readable; set only with `status: 'failed'`. */
    error?: string;
    attributedCount?: number;
    totalQuestions?: number;
  };
  /** Self-describing problems detected on this attempt. Already on the wire —
   *  `loadQSetSession` returns the raw doc — but historically admin-only, so
   *  nothing client-side read it. The solution-upload flow is the first thing to
   *  show one to the student. */
  issues?: SessionIssue[];
  /** Mirrors `QSetSession['status']` server-side. `discarded` is an attempt a
   *  person threw away with its work in front of them — distinct from
   *  `abandoned`, which is the system retiring one it believes to be empty.
   *  See docs/FeatureDesigns/assignments-tests/discard-attempt-target.md. */
  status: 'active' | 'completed' | 'abandoned' | 'discarded';
  /** When someone set this attempt aside, and who. Present on `discarded` rows,
   *  which the attempt list keeps and renders inert. */
  discardedAt?: string;
  discardedBy?: string;
  questionIds: string[];
  /** Phase 2: section structure snapshot. Absent ⇒ flat. */
  sections?: QuestionSetSection[];
  /** Tier 2: test-level default marking rule, snapshotted. */
  marking?: MarkingScheme;
  questionResults: QSetSessionQuestionResult[];
  /** Answers saved in this sitting and never submitted — `questionResults`
   *  only knows submitted work. Sent by `get`; see `SavedAnswer`. */
  savedAnswers?: SavedAnswer[];
  config: QuestionSetConfig;
  totalQuestions: number;
  startedAt: string;
  endedAt?: string;
  summary?: QSetSessionSummary;
}

export interface QSetAssignment {
  id: string;
  questionSetId?: string;           // present for test/assignment
  questionId?: string;              // present for quick-assign (individual question)
  questionSetTitle: string;
  /** How the paper plays, denormalized. Replaced `questionSetType`, the content
   *  label, in phase 3 of the question-set field migration. */
  questionSetModality?: 'practice' | 'test';
  studentId: string;
  assignedBy: string;
  assignedByName?: string;
  assignedByType: 'system' | 'teacher' | 'self' | 'other_student';
  subject?: string;
  totalQuestions?: number;
  /** Resolved topic (module) names the assignment covers — joined server-side
   *  from the question set's `topics` for list display. */
  topicNames?: string[];
  /** The question set's CURRENT publish status, joined server-side on
   *  `list-assigned`. The rest of this row is a snapshot taken when the
   *  assignment was created, so it cannot tell that a set has since been
   *  unpublished — and an unpublished set refuses to start. */
  questionSetStatus?: 'draft' | 'published' | 'archived';
  // Grant lifecycle, as stored by the server (functions/src/questionSet/types.ts).
  // This union previously read 'pending' | 'completed' | 'closed' — none of which
  // except 'closed' the server ever writes — so `status === 'pending'` compiled
  // fine and matched nothing. Keep in sync with the server.
  // 'declined' = student opted out; 'closed' = assigner closed it.
  status: 'active' | 'closed' | 'declined';
  declinedAt?: string;
  /** Local wall-clock deadline: 'YYYY-MM-DDTHH:mm', or 'YYYY-MM-DD' when no
   *  time is set (⇒ end of day). Format with `formatDue`, compare with
   *  `isPastDue`. See docs/FeatureDesigns/assignments-tests/assignment-due-time.md. */
  dueAt?: DueAt;
  instructions?: string;
  allowMultipleAttempts?: boolean;
  /** Per-assignment starting hint level (not a cap); 'none' disables.
   *  See docs/FeatureDesigns/assignments-tests/assignment-hint-level-target.md. */
  defaultHintLevel?: HintLevelSetting;
  allowAnalysisBeforeSubmit?: boolean;
  createdAt: string;
}

// DEFAULT_QUESTION_SET_CONFIG / DEFAULT_PRACTICE_SET_CONFIG retired (§12 Phase D).
// Backend MODALITY_PRESETS.test / .practice are the canonical play-config defaults.

export const READINESS_LEVELS = [
  { value: 0, label: 'Review Pending' },
  { value: 20, label: 'AI Reviewed' },
  { value: 30, label: 'Correctness Validated' },
  { value: 40, label: 'Complete' },
  { value: 50, label: 'Tested' },
  { value: 100, label: 'Needs Attention' },
  { value: -1, label: 'Rejected' },
  { value: -2, label: 'Duplicate' },
] as const;

export const READINESS_LABELS: Record<number, string> = Object.fromEntries(
  READINESS_LEVELS.map(r => [r.value, r.label])
);

export const RELATION_TYPE_OPTIONS = [
  { value: 'prerequisite', label: 'Prerequisite' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'similar-concept', label: 'Similar Concept' },
  { value: 'similar-question', label: 'Similar Question' },
  { value: 'harder-variant', label: 'Harder Variant' },
  { value: 'easier-variant', label: 'Easier Variant' },
  { value: 'same-exam', label: 'Same Exam' },
  { value: 'common-mistake-pair', label: 'Common Mistake Pair' },
  { value: 'duplicate', label: 'Duplicate' },
  // Set by the variation generator, not by hand — offered here so an editor can
  // read and correct one, not so a reviewer reaches for it as a label.
  { value: 'ai-gen-variation', label: 'AI-Generated Variation' },
] as const;

export const RELATION_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  RELATION_TYPE_OPTIONS.map(r => [r.value, r.label])
);

/**
 * The attempt's live "solve on paper → upload all", or undefined when there
 * isn't one.
 *
 * Discarded uploads keep their stamp — it is the only link from the attempt to
 * the scan's blobs, and nulling it once made a student's uploaded pages
 * unrecoverable while they still sat in storage. So presence of
 * `activeBulkUpload` no longer means "an upload is in flight"; ask this instead.
 *
 * See docs/FeatureDesigns/assignments-tests/student-work-durability.md → D3.
 */
export function liveBulkUpload(
  session: Pick<QSetSession, 'activeBulkUpload'> | null | undefined,
): QSetSession['activeBulkUpload'] | undefined {
  const upload = session?.activeBulkUpload;
  return upload && !upload.discardedAt ? upload : undefined;
}

/**
 * The attempt's uploads that can actually be shown. Mirrors the backend's
 * `viewableSolutionUploads` — keep the two in step, because the client decides
 * whether to OFFER the link and the server decides whether to SERVE it, and a
 * disagreement shows up as a "View uploaded solution (5 pages)" button that
 * answers "No uploaded solution for this session".
 *
 * Anything not discarded with a page to show: a finalized scan (manifest), or
 * one still being read (pages landed, `pageCount > 0`). The server serves the
 * latter from the pages it mirrors as they land, so a teacher watching the
 * two-minute split can already open what it is reading.
 */
export function viewableSolutionUploads(
  session: Pick<QSetSession, 'solutionUploads'> | null | undefined,
): SolutionUploadLink[] {
  return (session?.solutionUploads ?? []).filter(
    u => u.status !== 'discarded' && (!!u.manifestUrl || u.pageCount > 0),
  );
}

/** Page count for the newest viewable upload — what the "View uploaded solution"
 *  link renders. Undefined when there is nothing to show, which hides it. */
export function viewableUploadPageCount(
  session: Pick<QSetSession, 'solutionUploads'> | null | undefined,
): number | undefined {
  const uploads = viewableSolutionUploads(session);
  return uploads.length > 0 ? uploads[uploads.length - 1].pageCount : undefined;
}
